/**
 * useAccountComputedValues Hook
 * 
 * Computes derived values from accounts:
 * - Active and archived accounts
 * - Summary (assets, liabilities, total)
 * - Grouped accounts (by group, ordered)
 */

import { useMemo } from "react";
import { Account } from "../../../types";
import type { AccountGroupDoc } from "./useAccountGroups";
import { getCurrencyRate, calculateGroupBalance } from "../../../utils/accountHelpers";
import { Currency } from "../../../types";

export interface Summary {
  assets: number;
  liabilities: number;
  total: number;
}

export interface GroupedAccounts {
  [key: string]: Account[];
}

interface UseAccountComputedValuesParams {
  accounts: Account[];
  accountGroups: AccountGroupDoc[];
  defaultCurrency: string;
  currencies: Currency[];
}

interface UseAccountComputedValuesReturn {
  activeAccounts: Account[];
  archivedAccounts: Account[];
  summary: Summary;
  groupedActiveAccounts: GroupedAccounts;
  groupedArchivedAccounts: GroupedAccounts;
  getGroupBalance: (groupName: string) => number;
  getArchivedGroupBalance: (groupName: string) => number;
}

/**
 * Hook to compute derived values from accounts
 */
export function useAccountComputedValues({
  accounts,
  accountGroups,
  defaultCurrency,
  currencies,
}: UseAccountComputedValuesParams): UseAccountComputedValuesReturn {
  // Separate active and archived accounts
  const activeAccounts: Account[] = useMemo(() => {
    return accounts.filter((acc) => !acc.isArchived);
  }, [accounts]);

  const archivedAccounts: Account[] = useMemo(() => {
    return accounts.filter((acc) => acc.isArchived === true);
  }, [accounts]);

  // Calculate summary values (converted to default currency) - only for active accounts
  const summary: Summary = useMemo(() => {
    let assets = 0;
    let liabilities = 0;

    activeAccounts.forEach((acc) => {
      const accountAmount = parseFloat(String(acc.amount || 0));
      
      // Convert account amount to default currency
      let amountInDefaultCurrency = accountAmount;
      
      if (acc.currency !== defaultCurrency) {
        const accountCurrencyRate = getCurrencyRate(acc.currency, defaultCurrency, currencies);
        amountInDefaultCurrency = accountAmount / accountCurrencyRate;
      }

      if (amountInDefaultCurrency >= 0) {
        assets += amountInDefaultCurrency;
      } else {
        liabilities += Math.abs(amountInDefaultCurrency);
      }
    });

    const total = assets - liabilities;

    return { assets, liabilities, total };
  }, [activeAccounts, defaultCurrency, currencies]);

  // Group active accounts by group (ordered by accountGroups order, then by account order)
  const groupedActiveAccounts: GroupedAccounts = useMemo(() => {
    const grouped: GroupedAccounts = {};
    accountGroups.forEach((group) => {
      const groupAccounts = activeAccounts.filter((acc) => acc.group === group.name);
      // Sort by order field (default to 0 if not present)
      groupAccounts.sort((a, b) => {
        const orderA = (a as any).order ?? 0;
        const orderB = (b as any).order ?? 0;
        return orderA - orderB;
      });
      grouped[group.name] = groupAccounts;
    });
    return grouped;
  }, [activeAccounts, accountGroups]);

  // Group archived accounts by group (ordered by accountGroups order, then by account order)
  const groupedArchivedAccounts: GroupedAccounts = useMemo(() => {
    const grouped: GroupedAccounts = {};
    accountGroups.forEach((group) => {
      const groupAccounts = archivedAccounts.filter((acc) => acc.group === group.name);
      // Sort by order field (default to 0 if not present)
      groupAccounts.sort((a, b) => {
        const orderA = (a as any).order ?? 0;
        const orderB = (b as any).order ?? 0;
        return orderA - orderB;
      });
      grouped[group.name] = groupAccounts;
    });
    return grouped;
  }, [archivedAccounts, accountGroups]);

  // Calculate balance for a specific group (active accounts)
  const getGroupBalance = (groupName: string): number => {
    const groupAccounts = groupedActiveAccounts[groupName] || [];
    return calculateGroupBalance(groupAccounts, defaultCurrency, currencies);
  };

  // Calculate balance for a specific group (archived accounts)
  const getArchivedGroupBalance = (groupName: string): number => {
    const groupAccounts = groupedArchivedAccounts[groupName] || [];
    return calculateGroupBalance(groupAccounts, defaultCurrency, currencies);
  };

  return {
    activeAccounts,
    archivedAccounts,
    summary,
    groupedActiveAccounts,
    groupedArchivedAccounts,
    getGroupBalance,
    getArchivedGroupBalance,
  };
}
